/**
 * Define types related to fetching and updated xemplo global states here.
 * These types will be used acroos multiple applications
 */
export type XemploSessionData = {
  /** The Xemplo Identity session token */
  token: string;

  /** The DS ID of the selected persona */
  personaId: string;

  /**
   * The URL of the instance's HR module (see getHrModuleUrl); absent when the
   * instance has no HR module. The field name is a cross-app session-state
   * contract (shared with hr-fe and the mobile app) — do not rename it.
   */
  instanceUrl: string;

  /** The DS ID of the company */
  companyId: string;

  /** The Xemplo Identity ID of the session */
  sessionId: string;

  /**
   * The origin the persona was selected from, i.e. the writer's window location origin.
   *
   * Still written by every persona-switch site because legacy hr-fe reads it, but the
   * React apps ignore it: this record is the only source of truth for the active
   * persona, and it is adopted unconditionally — see ADR 0013 and `useSessionRecord`.
   */
  origin: string;
};

/**
 * Available scopes for Xemplo state management.
 *
 * These scopes determine the lifetime and visibility of state data:
 * - **All**: Application-wide data, shared across all users and sessions
 * - **Session**: Temporary data for the current session, cleared on logout
 * - **User**: User-specific data, persisted across sessions (not persona-specific)
 * - **Persona**: User data tied to a specific persona, persisted across sessions
 */
export const XemploStateScopes = {
  /**
   * Put in here data that relates to
   * all scopes and levels and is not
   * really related to user or session.
   *
   * e.g.: Application-wide settings
   */
  All: 'All',
  /**
   * Put in here data that relates to the current
   * session and should be wiped when the session is over.
   *
   * e.g.: Auth tokens, session identifiers, etc.
   */
  Session: 'Session',

  /**
   * This is data that is related to the user
   * and can be persisted between sessions and
   * it's NOT tied to the current persona.
   *
   * e.g.: Global user preferences
   */
  User: 'User',

  /**
   * This is data that is related to the user
   * and can be persisted between sessions and
   * it's tied to the current persona.
   *
   * e.g.: Persona-specific preferences
   */
  Persona: 'Persona',
} as const;

/**
 * Type representing a valid Xemplo state scope value.
 * @see XemploStateScopes
 */
export type XemploStateScope = (typeof XemploStateScopes)[keyof typeof XemploStateScopes];
